import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, orderBy, getDocs, doc, updateDoc, where, addDoc, getDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db, getNextPoNumber, addOrderToDb } from '../firebase';
import { buildQuoteConversionNotes } from '../utils/orderPoNumber';
import { DOC_TYPES, QUOTE_STATUSES } from '../firebase/schema';
import { sendQuoteEmail } from '../utils/quoteUtils';
import styled from '@emotion/styled';
import { formatCurrency } from '../utils/formatters';
import { formatDate } from '../utils/dateFormatter';
import Modal from '../components/Modal';
import NewQuote from './NewQuote';
import OrderView from '../components/OrderView';
import { FaEye, FaPencilAlt, FaTrash, FaDownload } from 'react-icons/fa';
import Quote from '../components/Quote';
import html2pdf from 'html2pdf.js';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

const QuotesContainer = styled.div`
  padding: 80px 1% 20px 1%;
  width: 98%;
  margin: 0 auto;
  background: var(--al-bg);
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  color: white;
`;

const PageHeader = styled.h1`
  font-family: 'Montserrat', sans-serif;
  color: white;
  text-transform: uppercase;
  font-size: 24px;
  margin-bottom: 20px;
  font-weight: 600;
  letter-spacing: 0.05em;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CreateButton = styled.button`
  background: var(--al-primary);
  color: black;
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-size: 0.8rem;
  
  &:hover {
    background: #7FFF7F;
  }
`;

const QuotesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  font-family: 'Montserrat', sans-serif;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: white;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.02em;
`;

const TableRow = styled.tr`
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    cursor: pointer;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
    case QUOTE_STATUSES.DRAFT:
      return '#666';
    case QUOTE_STATUSES.SENT:
      return 'var(--al-primary)';
    case QUOTE_STATUSES.PENDING_APPROVAL:
      return '#FFD700';
    case QUOTE_STATUSES.APPROVED:
      return '#00CED1';
    case QUOTE_STATUSES.CONVERTED:
      return '#4CAF50';
    case QUOTE_STATUSES.EXPIRED:
      return '#FF6B6B';
    case QUOTE_STATUSES.DECLINED:
      return '#8B0000';
    default:
      return '#666';
  }
  }};
  color: white;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  color: white;
  padding: 6px;
  margin-right: 8px;
  cursor: pointer;
  font-size: 0.8em;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &.convert {
    background: var(--al-primary);
    color: black;
    border: none;
    padding: 4px 8px;
    margin-left: 8px;
    
    &:hover {
      background: #7FFF7F;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const ModalContent = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  color: white;
  margin-bottom: 20px;
  font-size: 1.2em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.secondary ? 'transparent' : 'var(--al-primary)'};
  border: 1px solid ${props => props.secondary ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.secondary ? 'white' : 'var(--al-bg)'};
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  min-width: 120px;
  
  &:hover {
    background: ${props => props.secondary ? 'rgba(255, 255, 255, 0.1)' : '#7ACC7A'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const EmailPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const EmailPopupContent = styled.div`
  background: var(--al-surface);
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 400px; // Smaller width for this specific popup
  border-radius: 4px;
`;

const PopupTitle = styled.h2`
  color: white;
  margin-bottom: 20px;
  font-size: 1.2em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PopupText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const PopupButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.secondary ? 'transparent' : 'var(--al-primary)'};
  border: 1px solid ${props => props.secondary ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.secondary ? 'white' : 'var(--al-bg)'};
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.secondary ? 'rgba(255, 255, 255, 0.1)' : '#7ACC7A'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Quotes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { facilityId } = useFacility();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [orderViewData, setOrderViewData] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [quoteToDownload, setQuoteToDownload] = useState(null);
  const [quoteToEdit, setQuoteToEdit] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const quoteRef = React.useRef(null);
  const [quotePdfActionQuote, setQuotePdfActionQuote] = useState(null);
  const [customerEmailForQuote, setCustomerEmailForQuote] = useState(null);
  const [quotePdfActionCustomerLoading, setQuotePdfActionCustomerLoading] = useState(false);
  const [quoteEmailSending, setQuoteEmailSending] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, [facilityId]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const quotesRef = collection(db, 'quotes');
      const q = query(
        quotesRef,
        where('type', '==', DOC_TYPES.QUOTE),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const quotesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setQuotes(filterByFacility(quotesData, facilityId));
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToOrder = async (quote) => {
    if (quote.convertedOrderId || quote.status === QUOTE_STATUSES.CONVERTED) {
      alert('This quote was already converted to an order.');
      return;
    }

    try {
      // Always issue a new sequential invoice/PO number (ignore quote-style IDs from the website)
      const poNumber = await getNextPoNumber();

      const linesRef = collection(db, 'quoteLines');
      const q = query(
        linesRef,
        where('quoteId', '==', quote.id),
        orderBy('lineNumber', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const quoteLines = querySnapshot.docs.map((lineDoc) => ({
        id: lineDoc.id,
        ...lineDoc.data()
      }));

      if (quoteLines.length === 0) {
        alert('Cannot convert: this quote has no line items.');
        return;
      }

      const expiresAt = quote.expiresAt?.toDate?.() || quote.expiresAt;
      const dueDate = expiresAt instanceof Date && !Number.isNaN(expiresAt.getTime())
        ? expiresAt
        : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

      const result = await addOrderToDb({
        poNumber,
        projectManager: quote.projectManager || '',
        customerId: quote.customerId,
        customerName: quote.customerName,
        dueDate,
        notes: buildQuoteConversionNotes(quote),
        totalQuantity: quote.totalQuantity,
        subtotal: quote.subtotal,
        orderTotal: quote.subtotal,
        cogsTotal: quote.cogsTotal,
        cogsPercentage: quote.cogsPercentage,
        extraFees: quote.extraFees || 0,
        fee1Type: quote.fee1Type || '',
        fee1Quantity: quote.fee1Quantity || 0,
        fee1Price: quote.fee1Price || 0,
        fee2Type: quote.fee2Type || '',
        fee2Quantity: quote.fee2Quantity || 0,
        fee2Price: quote.fee2Price || 0,
        fees: Array.isArray(quote.fees) ? quote.fees : undefined,
        discount: quote.discount || undefined,
        additionalInstructions: quote.additionalInstructions || undefined,
        taxExempt: quote.taxExempt || false,
        lines: quoteLines.map((line, index) => ({
          designName: line.designName,
          garmentId: line.garmentId,
          garmentColor: line.garmentColor,
          sizes: line.sizes,
          pricePerPiece: line.pricePerPiece,
          cogs: line.cogs,
          frontInkColors: line.frontInkColors || [],
          backInkColors: line.backInkColors || [],
          otherInkColors: line.otherInkColors || [],
          printLocations: line.printLocations || [],
          sizeType: line.sizeType || 'unisex',
          type: line.type || 'screen',
          files: line.files,
          designFileUrl: line.designFileUrl,
          lineNumber: line.lineNumber ?? index + 1
        }))
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create order');
      }

      const orderId = result.orderId;

      await updateDoc(doc(db, 'orders', orderId), {
        type: DOC_TYPES.ORDER,
        convertedFromQuote: quote.id,
        quoteNumber: quote.quoteNumber || null,
        updatedAt: serverTimestamp()
      });

      await updateDoc(doc(db, 'quotes', quote.id), {
        status: QUOTE_STATUSES.CONVERTED,
        convertedOrderId: orderId,
        convertedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await fetchQuotes();

      alert(
        `Order created with invoice #${poNumber}` +
          (quote.quoteNumber ? ` (quote ${quote.quoteNumber})` : '')
      );
      navigate('/orders');
    } catch (error) {
      console.error('Error converting quote to order:', error);
      alert(`Error converting quote to order: ${error.message}`);
    }
  };

  const handleViewQuote = async (quote) => {
    try {
      // Fetch the quote's lines
      const linesRef = collection(db, 'quoteLines');
      const q = query(
        linesRef,
        where('quoteId', '==', quote.id),
        orderBy('lineNumber', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrderViewData({
        ...quote,
        lines: lines
      });
    } catch (error) {
      console.error('Error fetching quote lines:', error);
      setOrderViewData({
        ...quote,
        lines: []
      });
    }
  };

  const handleCloseModal = () => {
    setQuoteToEdit(null);
    setIsModalOpen(false);
  };

  // Filter out converted quotes in the render
  const activeQuotes = quotes.filter(quote => quote.status !== QUOTE_STATUSES.CONVERTED);

  // Edit handler: load quote lines and open NewQuote in edit mode
  const handleEditQuote = async (quote) => {
    try {
      const linesRef = collection(db, 'quoteLines');
      const q = query(
        linesRef,
        where('quoteId', '==', quote.id),
        orderBy('lineNumber', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuoteToEdit({ ...quote, lines });
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error loading quote for edit:', err);
      alert('Failed to load quote for editing: ' + (err.message || err));
    }
  };

  // Delete handler
  const handleConfirmDelete = async () => {
    if (!quoteToDelete) return;
    
    try {
      // First delete all quote lines
      const linesRef = collection(db, 'quoteLines');
      const q = query(linesRef, where('quoteId', '==', quoteToDelete.id));
      const querySnapshot = await getDocs(q);
      
      const deleteLinePromises = querySnapshot.docs.map(lineDoc => 
        deleteDoc(doc(db, 'quoteLines', lineDoc.id))
      );
      await Promise.all(deleteLinePromises);
      
      // Then delete the quote itself
      await deleteDoc(doc(db, 'quotes', quoteToDelete.id));
      
      // Refresh the quotes list
      await fetchQuotes();
      
      setQuoteToDelete(null);
      alert('Quote deleted successfully');
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Failed to delete quote: ' + error.message);
    }
  };

  // Open PDF action modal (download or email)
  const handleOpenQuotePdfModal = (quote) => {
    setQuotePdfActionQuote(quote);
    setCustomerEmailForQuote(null);
    setQuotePdfActionCustomerLoading(!!quote.customerId);
  };

  // Fetch customer email when PDF action modal opens
  React.useEffect(() => {
    if (!quotePdfActionQuote?.customerId) {
      setQuotePdfActionCustomerLoading(false);
      setCustomerEmailForQuote(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const customerSnap = await getDoc(doc(db, 'customers', quotePdfActionQuote.customerId));
        if (cancelled) return;
        setCustomerEmailForQuote(customerSnap.exists && customerSnap.data().email ? customerSnap.data().email : null);
      } catch (err) {
        if (!cancelled) setCustomerEmailForQuote(null);
      } finally {
        if (!cancelled) setQuotePdfActionCustomerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quotePdfActionQuote?.customerId]);

  const fetchQuoteLines = async (quote) => {
    const linesRef = collection(db, 'quoteLines');
    const q = query(
      linesRef,
      where('quoteId', '==', quote.id),
      orderBy('lineNumber', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  };

  const handlePdfDownload = async () => {
    if (!quotePdfActionQuote) return;
    try {
      const lines = await fetchQuoteLines(quotePdfActionQuote);
      setQuoteToDownload({ ...quotePdfActionQuote, lines });
      setQuotePdfActionQuote(null);
    } catch (error) {
      console.error('Error fetching quote lines:', error);
      alert('Failed to prepare quote for download');
    }
  };

  const handlePdfEmail = async () => {
    if (!quotePdfActionQuote || !customerEmailForQuote) return;
    try {
      const lines = await fetchQuoteLines(quotePdfActionQuote);
      setQuoteEmailSending(true);
      setQuoteToDownload({ ...quotePdfActionQuote, lines, action: 'email', to: customerEmailForQuote });
      setQuotePdfActionQuote(null);
    } catch (error) {
      console.error('Error fetching quote lines:', error);
      alert('Failed to prepare quote for email');
      setQuoteEmailSending(false);
    }
  };

  // Effect to generate PDF once the Quote component is rendered (download or email)
  React.useEffect(() => {
    const action = quoteToDownload?.action;
    const isEmail = action === 'email';
    const toEmail = quoteToDownload?.to;
    if (!quoteToDownload || !quoteRef.current) return;

    setDownloading(true);

    const generatePdf = async () => {
      try {
        const opt = {
          margin: 0.5,
          filename: `Quote_${quoteToDownload.quoteNumber || 'draft'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        if (isEmail && toEmail) {
          const pdfBlob = await html2pdf().set(opt).from(quoteRef.current).output('blob');
          const pdfBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result && reader.result.split(',')[1]) || '');
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          await sendQuoteEmail({
            to: toEmail,
            customerName: quoteToDownload.customerName || 'Customer',
            quoteNumber: quoteToDownload.quoteNumber || 'draft',
            pdfContent: pdfBase64,
            createdAt: formatDate(quoteToDownload.createdAt)
          });
          alert('Quote sent successfully.');
        } else {
          await html2pdf().set(opt).from(quoteRef.current).save();
        }
        setQuoteToDownload(null);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert(isEmail ? 'Failed to send email' : 'Failed to generate PDF');
      } finally {
        setDownloading(false);
        setQuoteEmailSending(false);
      }
    };

    setTimeout(generatePdf, 1500);
  }, [quoteToDownload]);


  if (loading) {
    return (
      <QuotesContainer>
        <div>Loading quotes...</div>
      </QuotesContainer>
    );
  }

  return (
    <QuotesContainer>
      <HeaderContainer>
        <PageHeader>QUOTES</PageHeader>
        <CreateButton onClick={() => { setQuoteToEdit(null); setIsModalOpen(true); }}>
            Create Quote
        </CreateButton>
      </HeaderContainer>

      <QuotesTable>
        <thead>
          <tr>
            <Th>Quote #</Th>
            <Th>Customer</Th>
            <Th>Created</Th>
            <Th>Expires</Th>
            <Th>Total</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {activeQuotes.map((quote) => (
                <TableRow 
                  key={quote.id}
              onClick={() => handleViewQuote(quote)}
                >
              <Td>{quote.quoteNumber}</Td>
              <Td>{quote.customerName}</Td>
              <Td>{formatDate(quote.createdAt)}</Td>
              <Td>{formatDate(quote.expiresAt)}</Td>
              <Td>{formatCurrency(quote.subtotal)}</Td>
              <Td onClick={e => e.stopPropagation()}>
                <ActionButtonsContainer>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewQuote(quote);
                    }}
                    title="View Quote"
                  >
                    <FaEye />
                  </ActionButton>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditQuote(quote);
                    }}
                    title="Edit Quote"
                  >
                    <FaPencilAlt />
                  </ActionButton>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenQuotePdfModal(quote);
                    }}
                    title="Download or email Quote PDF"
                    disabled={downloading}
                  >
                    <FaDownload />
                  </ActionButton>
                  <ActionButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuoteToDelete(quote);
                    }}
                    title="Delete Quote"
                  >
                    <FaTrash />
                  </ActionButton>
                      {quote.status !== QUOTE_STATUSES.CONVERTED && (
                    <ActionButton 
                      className="convert"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConvertToOrder(quote);
                      }}
                        >
                          Convert to Order
                    </ActionButton>
                      )}
                </ActionButtonsContainer>
              </Td>
                </TableRow>
              ))}
        </tbody>
      </QuotesTable>

      {orderViewData && (
        <OrderView 
          order={orderViewData} 
          onClose={() => setOrderViewData(null)}
        />
      )}

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} maxWidth="1200px">
        <NewQuote
          onClose={handleCloseModal}
          initialQuote={quoteToEdit}
          onSuccess={() => { handleCloseModal(); fetchQuotes(); }}
        />
      </Modal>

      {/* PDF action modal: Download or Email */}
      {quotePdfActionQuote && (
        <Modal isOpen={!!quotePdfActionQuote} onClose={() => setQuotePdfActionQuote(null)}>
          <EmailPopupContent onClick={e => e.stopPropagation()}>
            <PopupTitle>Quote PDF</PopupTitle>
            <PopupText style={{ marginBottom: 8 }}>
              {quotePdfActionQuote.quoteNumber || quotePdfActionQuote.id?.slice(0, 8)}
            </PopupText>
            {quotePdfActionCustomerLoading ? (
              <PopupText>Loading customer...</PopupText>
            ) : customerEmailForQuote ? (
              <PopupText>
                Email to: <strong>{customerEmailForQuote}</strong>
              </PopupText>
            ) : (
              <PopupText>
                No customer email on file. Add an email in the customer record to send the quote.
              </PopupText>
            )}
            <ButtonGroup>
              <PopupButton secondary onClick={() => setQuotePdfActionQuote(null)}>
                Cancel
              </PopupButton>
              <PopupButton onClick={handlePdfDownload}>Download PDF</PopupButton>
              <PopupButton
                onClick={handlePdfEmail}
                disabled={!customerEmailForQuote || quoteEmailSending}
                style={{ opacity: (!customerEmailForQuote || quoteEmailSending) ? 0.6 : 1 }}
              >
                {quoteEmailSending ? 'Sending…' : 'Email to customer'}
              </PopupButton>
            </ButtonGroup>
          </EmailPopupContent>
        </Modal>
      )}

      {/* Hidden Quote component for PDF generation */}
      {quoteToDownload && (
        <div style={{ position: 'fixed', left: '100vw', top: 0, width: '8.5in', background: 'white', zIndex: -1 }}>
          <div ref={quoteRef}>
            <Quote quote={quoteToDownload} hideActions={true} />
          </div>
        </div>
      )}

      {/* Loading overlay while downloading */}
      {downloading && (
        <EmailPopupOverlay>
          <EmailPopupContent>
            <PopupTitle>Generating PDF...</PopupTitle>
            <PopupText>Please wait while your quote is being prepared for download.</PopupText>
          </EmailPopupContent>
        </EmailPopupOverlay>
      )}

      {/* Delete confirmation modal */}
      {quoteToDelete && (
        <EmailPopupOverlay onClick={() => setQuoteToDelete(null)}>
          <EmailPopupContent onClick={e => e.stopPropagation()}>
            <PopupTitle>Delete Quote?</PopupTitle>
            <PopupText>
              Are you sure you want to delete quote <strong>{quoteToDelete.quoteNumber}</strong> for {quoteToDelete.customerName}?
              <br /><br />
              This action cannot be undone.
            </PopupText>
            <ButtonGroup>
              <PopupButton
                secondary
                onClick={() => setQuoteToDelete(null)}
              >
                Cancel
              </PopupButton>
              <PopupButton
                onClick={handleConfirmDelete}
                style={{ background: '#FF6B6B' }}
              >
                Delete Quote
              </PopupButton>
            </ButtonGroup>
          </EmailPopupContent>
        </EmailPopupOverlay>
      )}
    </QuotesContainer>
  );
};

export default Quotes; 